import { useController } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { DisplayModes } from 'services/webedi/types';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { SchemableFormCheckboxGroup } from 'components/Form/SchemableFormCheckboxGroup/SchemableFormCheckboxGroup';
import { useInterpolatedEnumerableOptions } from 'components/SchemableForm/hooks/useInterpolatedEnumerableOptions';

type MultiSelectFieldProps = {
  field: ui.EnumerableField;
  fieldId: string;
  isReadOnly: boolean;
  mode: DisplayModes;
  variant?: 'large';
};

export const MultiSelectField = ({ field, fieldId, isReadOnly, mode, variant }: MultiSelectFieldProps) => {
  const { field: formField } = useController({ name: fieldId });
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const fieldState = service.useFieldStateFromAnnotations(fieldId);
  const popoverItem = service.useItemByFieldPath(fieldId);
  const setCurrentItem = service.setCurrentItem;
  const { options } = useInterpolatedEnumerableOptions(field.type_config);

  if (mode === DisplayModes.view) {
    const selectedValues: Array<string> = formField.value ?? [];
    const selectedLabels = selectedValues.map((v) => options.find((o) => o.value === v)?.label).filter(Boolean);

    return (
      <div className="flex flex-col gap-2">
        <span className="text-xs text-gray-500">{field.label}</span>
        {selectedLabels.length > 0 ? (
          <ul className="list-disc pl-4">
            {selectedLabels.map((label, index) => (
              <li key={index} className="text-sm text-gray-700">
                {label}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-gray-400">&mdash;</span>
        )}
      </div>
    );
  }

  return (
    <SchemableFormCheckboxGroup
      field={field}
      value={formField.value ?? []}
      onChange={formField.onChange}
      onBlur={formField.onBlur}
      error={popoverItem}
      onShowFieldErrorModal={() => popoverItem && setCurrentItem(popoverItem)}
      required={field.modality === 'MANDATORY'}
      disabled={isReadOnly}
      fieldState={fieldState}
      variant={variant}
      name={formField.name}
    />
  );
};
